import { useEffect, useRef, useState } from 'react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ApiCallError, scanLicence, enterLicenceManually, getCheckInIdentity } from '@/api/client';
import type { CheckInDto, DriverIdentityDto, LicenceFindingDto } from '@/api/types';

/**
 * LICENCE SCAN — the scheduler's desk, not the gate.
 *
 * ⭐ WHY THE HUMAN MATTERS. Scanning here rather than on the kiosk is the whole
 * design (DESIGN-driver-identity-and-paperwork-capture.md §0): a scheduler holds
 * the card, looks at the photograph, and looks at the man. That is the only step
 * in the system that PREVENTS a fraudulent pickup. Everything this dialog stores
 * is evidence for afterwards; the prevention is the person reading the screen.
 *
 * ⚠️ THE ZEBRA DS22 IS A KEYBOARD. It decodes the PDF417 on the BACK of the
 * licence and types the result. So there is no device code here at all — just a
 * focused box. If the box is not focused, the barcode goes wherever the cursor
 * is, which is why it takes focus on open and takes it back after every action.
 *
 * ⛔ AND THE BOX IS A TEXTAREA, NOT AN INPUT, WHICH IS NOT A STYLE CHOICE.
 *
 * An AAMVA barcode IS a multi-line record: the LF between elements is the only
 * thing that says where one field stops and the next starts. A single-line
 * `<input>` cannot hold a newline — the browser drops it, and Enter fires
 * submit. So the old box could not physically carry a valid licence:
 *
 *   - with the scanner sending CR/LF (correct), the FIRST line break submitted
 *     the form, and the server received about one character;
 *   - with CR/LF suppressed to stop that, every field boundary was gone, the
 *     parser returned the whole remaining string for each field, and the write
 *     died in the SQL driver with error 8016 — "Internal server error" at the
 *     desk (2026-08-25).
 *
 * There was no scanner setting that worked, which is why fixing the setting
 * kept moving the failure around instead of removing it.
 *
 * ⭐ SO THE BURST ENDS ON TIME, NOT ON A KEY. A wedge types the whole barcode in
 * a few milliseconds per character; a pause means the burst is over. Enter is
 * therefore just another character inside the record, which is exactly what it
 * is in the barcode.
 *
 * ---- ⛔ AND THE BROWSER EATS THE SEPARATORS UNLESS WE CATCH THEM -------------
 *
 * ⭐ PROVEN 2026-08-25, and it is not what it looked like. The same scan into
 * NOTEPAD comes out perfectly formed — `@`, the ANSI header, then every element
 * on its own line. The scanner is doing its job. But a keyboard has no
 * line-feed key, so Zebra sends ASCII below 32 as CONTROL-KEY SEQUENCES: LF
 * arrives as Ctrl+J. Notepad honours that and breaks the line; CHROME TREATS
 * Ctrl+J AS "OPEN DOWNLOADS" AND SWALLOWS IT, so the textarea receives only the
 * printable characters and the whole record lands as one unbroken line.
 *
 * ⚠️ WHICH IS WHY EVERY SCANNER SETTING FAILED. "Add Enter Key" and "Add Tab"
 * are SUFFIXES — they append one character to the END of the transmission and
 * cannot touch the separators between fields. There was nothing wrong to fix on
 * the gun.
 *
 * ⭐ SO WE CATCH Ctrl+J OURSELVES, and that is the whole point of doing it here
 * rather than in the scanner: it needs NO device configuration, so it cannot be
 * defeated by one mis-flashed gun at one site — which was the standing
 * objection to fixing this in the hardware (#226a).
 *
 * ⛔ onKeyDown IS THEREFORE BACK, AND IT STILL MUST NEVER SUBMIT. It inserts a
 * newline and nothing else. Submitting on a key is the original #226a bug, and
 * `sql-bind-width-guard.cjs` section 7 now asserts the narrower rule: a keydown
 * handler may exist, but it may not call `submitScan` and may not look at Enter.
 *
 * ⛔ THE SCANNED STRING CONTAINS DATE OF BIRTH AND HOME ADDRESS. It is masked on
 * screen, cleared the moment it is sent, never logged, and never rendered back.
 * The server stores it; nothing in the browser keeps it.
 */

interface Props {
    open: boolean;
    checkIn: CheckInDto | null;
    getToken: () => Promise<string>;
    onClose: () => void;
    /**
     * A licence was stored — the panel should reload so the card reflects it.
     *
     * ⭐ THE IDENTITY IS HANDED BACK, and it matters. The caller opens "Which load
     * is this?" straight after, and it was rendering the arrival AS IT WAS BEFORE
     * the scan — so the dialog said "Driver not named" seconds after the licence
     * told us his name (#235). Passing the identity means the next screen can use
     * what we just learned instead of waiting for a reload to land.
     */
    onSaved: (identity: DriverIdentityDto | null) => void;
}

type Mode = 'scan' | 'manual';

const FINDING_TONE = (f: LicenceFindingDto) =>
    f.requiresOverride
        ? 'border-amber-400 bg-amber-50 text-amber-900'
        : 'border-slate-200 bg-slate-50 text-slate-700';

/** ⚠️ Only ever shows what the SERVER returned. There is no client-side parsing. */
function IdentityCard({ identity }: { identity: DriverIdentityDto }) {
    return (
        <div className="rounded border bg-background p-2 text-xs">
            <div className="text-sm font-medium">{identity.displayName ?? 'No name on the license'}</div>
            <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-muted-foreground">
                <span>License</span>
                <span className="font-mono">
                    {identity.licenceNumber ?? '—'}
                    {identity.licenceState ? ` · ${identity.licenceState}` : ''}
                </span>
                <span>Expires</span>
                <span>{identity.licenceExpiry ?? 'not readable'}</span>
                <span>Class</span>
                <span>
                    {identity.vehicleClass ?? '—'}
                    {identity.endorsements ? ` · endorsements ${identity.endorsements}` : ''}
                    {identity.restrictions ? ` · restrictions ${identity.restrictions}` : ''}
                </span>
                <span>Captured</span>
                <span>
                    {identity.isManualEntry ? 'typed by staff' : 'scanned'}
                    {identity.scannedBy ? ` · ${identity.scannedBy}` : ''}
                </span>
            </div>
        </div>
    );
}

export function LicenceScanDialog({ open, checkIn, getToken, onClose, onSaved }: Props) {
    const [mode, setMode] = useState<Mode>('scan');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /**
     * ⭐⭐ THE SHAPE OF A REFUSED SCAN (2026-09-24).
     *
     * Pete: *"some licenses are scanning and some are not ... It has been Texas
     * and Kansas. Colorado is working fine."*
     *
     * ⛔ A FAILED SCAN STORES NOTHING - `RawBarcode` is written only on success -
     * so every report of this cost another round of guessing. The server now
     * returns a one-line structural description with the refusal.
     *   ** A FAILURE PATH THAT KEEPS NO RECORD MAKES EVERY OCCURRENCE THE FIRST
     *      OCCURRENCE. **
     * ⚠️ NO VALUES IN IT. Lengths, counts, element codes and the issuing
     * jurisdiction's IIN - safe to paste into a message, which is the only
     * reason it will ever actually be sent.
     */
    const [shape, setShape] = useState<string | null>(null);
    const [shapeCopied, setShapeCopied] = useState(false);
    /**
     * ⛔ Set only when the server has told us this arrival already belongs to a
     * DIFFERENT person. Holding the pending value here — rather than re-reading
     * the input — means the confirm replays exactly what was scanned, not
     * whatever the field happens to contain a moment later.
     */
    const [replace, setReplace] = useState<{ message: string; retry: (c: boolean) => Promise<void> } | null>(null);

    const [identity, setIdentity] = useState<DriverIdentityDto | null>(null);
    const [findings, setFindings] = useState<LicenceFindingDto[]>([]);
    const [recognised, setRecognised] = useState(false);
    const [chars, setChars] = useState(0);
    /**
     * ⭐ THE LINE COUNT IS THE DIAGNOSTIC, and it is on screen because the
     * failure it detects is invisible otherwise. A licence that arrives as ONE
     * line is the Ctrl+J fault; a real record is ~25-30 lines. Showing it means
     * a scheduler — or Pete at a scanner menu — can tell instantly whether the
     * separators are landing, without submitting anything or reading a log.
     */
    const [lineCount, setLineCount] = useState(0);

    // Manual fields.
    const [mFirst, setMFirst] = useState('');
    const [mLast, setMLast] = useState('');
    const [mNumber, setMNumber] = useState('');
    const [mState, setMState] = useState('');
    const [mExpiry, setMExpiry] = useState('');
    const [mClass, setMClass] = useState('');

    const scanRef = useRef<HTMLTextAreaElement>(null);
    /**
     * ⚠️ A TIMER, NOT STATE. Re-rendering on every one of ~250 keystrokes to
     * track a deadline would be the one thing a wedge scanner is fast enough to
     * outrun, and a stale closure over a state value would end the burst against
     * a length measured several hundred characters ago.
     */
    const burstTimer = useRef<number | null>(null);
    /**
     * ⚠️ NOT REACT STATE. The scanned string must not sit in a state value that
     * React keeps for the lifetime of the component, get captured by a closure,
     * or show up in a devtools props tree. It lives in the DOM input, is read
     * once on submit, and is wiped immediately.
     */
    const readAndWipe = (): string => {
        const el = scanRef.current;
        if (!el) return '';
        const v = el.value;
        el.value = '';
        setChars(0);
        setLineCount(0);
        // ⚠️ trim() ONLY. Never a newline strip: the interior line breaks ARE
        // the field boundaries, and removing them is precisely the fault this
        // box was rebuilt to stop. The header begins '@', so nothing at the
        // front is whitespace and only the scanner's trailing CR is lost.
        return v.trim();
    };

    /**
     * ⭐ END OF BURST = A PAUSE.
     *
     * A DS22 types its characters microseconds apart, so any real gap means it
     * has finished. 180ms is far longer than any inter-character gap and far
     * shorter than a person notices — the scan feels instant and the scheduler
     * never presses anything.
     *
     * ⛔ IT WILL NOT SUBMIT A FRAGMENT. A burst that does not contain the AAMVA
     * header is left in the box, because a partial read that auto-submitted
     * would send the TAIL of a licence — which the server would reject as "not a
     * licence" and which would train a scheduler to ignore that message. Save
     * stays available for that case, and the manual path is one click away.
     */
    const BURST_GAP_MS = 180;

    function clearBurst() {
        if (burstTimer.current !== null) {
            window.clearTimeout(burstTimer.current);
            burstTimer.current = null;
        }
    }

    /**
     * ⛔ TURN THE WEDGE'S CONTROL-KEY SEQUENCES BACK INTO LINE BREAKS.
     *
     * Ctrl+J is LF and Ctrl+M is CR — the two separators an AAMVA record is
     * built from. `preventDefault` is what stops Chrome opening its Downloads
     * panel on Ctrl+J; without it the keystroke is consumed by the browser and
     * the field boundary is lost, which is the entire bug.
     *
     * ⛔ IT INSERTS. IT DOES NOT SUBMIT. The burst still ends on a pause.
     *
     * ⚠️ THE BOX IS UNCONTROLLED ON PURPOSE — the scanned string must not live
     * in React state where a closure or a devtools tree can hold it. So writing
     * `el.value` here does NOT fire React's onChange, and `onScanInput` has to
     * be called by hand or the character count and the burst timer would both
     * stop moving halfway through a licence.
     */
    function onScanKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
        if (!e.ctrlKey || e.altKey || e.metaKey) return;
        const k = e.key.toLowerCase();
        if (k !== 'j' && k !== 'm') return;

        e.preventDefault();
        const el = e.currentTarget;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? start;
        el.value = el.value.slice(0, start) + '\n' + el.value.slice(end);
        el.selectionStart = el.selectionEnd = start + 1;
        onScanInput(el.value);
    }

    function onScanInput(v: string) {
        setChars(v.length);
        setLineCount(v ? v.split('\n').length : 0);
        clearBurst();
        if (!v) return;
        burstTimer.current = window.setTimeout(() => {
            burstTimer.current = null;
            // Read the LIVE value, not the one captured when the timer was set.
            const now = scanRef.current?.value ?? '';
            if (/ANSI |AAMVA/.test(now)) void submitScan(false);
        }, BURST_GAP_MS);
    }

    // Fresh state on every open — a previous driver's licence must not still be
    // on screen when the next one walks up.
    useEffect(() => {
        if (!open) return;
        setMode('scan');
        setError(null);
        setReplace(null);
        setIdentity(null);
        setFindings([]);
        setRecognised(false);
        setChars(0);
        setLineCount(0);
        setMFirst(''); setMLast(''); setMNumber(''); setMState(''); setMExpiry(''); setMClass('');
        if (scanRef.current) scanRef.current.value = '';

        // Show what we already hold, so a scheduler can see this driver is known
        // before scanning anything.
        if (!checkIn) return;
        let cancelled = false;
        void (async () => {
            try {
                const res = await getCheckInIdentity(await getToken(), checkIn.checkInId);
                if (cancelled) return;
                setIdentity(res.identity);
                setFindings(res.identity ? res.findings : []);
            } catch {
                // Not fatal: the point of the dialog is to CAPTURE a licence, and
                // failing to show a prior one must not stop that.
            }
        })();
        return () => { cancelled = true; };
    }, [open, checkIn, getToken]);

    /**
     * ⛔ A PENDING BURST MUST NOT OUTLIVE THE DIALOG. A timer that fires after
     * the scheduler has closed this would post a half-read barcode against an
     * arrival nobody is looking at any more, and the result would appear on a
     * card with no explanation of where it came from.
     */
    useEffect(() => {
        if (open) return;
        if (burstTimer.current !== null) {
            window.clearTimeout(burstTimer.current);
            burstTimer.current = null;
        }
    }, [open]);

    useEffect(() => () => {
        if (burstTimer.current !== null) window.clearTimeout(burstTimer.current);
    }, []);

    // The scanner types into whatever has focus. Take it, and take it back.
    useEffect(() => {
        if (open && mode === 'scan') {
            const t = setTimeout(() => scanRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [open, mode, busy, identity]);

    function describe(err: unknown): string {
        if (err instanceof ApiCallError) return err.details.error;
        return 'Something went wrong saving the license.';
    }

    async function submitScan(confirmed: boolean, presetRaw?: string) {
        if (!checkIn) return;
        const raw = presetRaw ?? readAndWipe();
        if (!raw) {
            /**
             * ⛔ NEVER CRY WOLF OVER A LICENCE THAT IS ALREADY SAVED. Pete pressed
             * Save after a successful scan and got this in red: *"Are we saving what
             * is on the screen that was scanned or if we close, does that save?"*
             * (#236). The burst had already submitted and `readAndWipe` had emptied
             * the box, so Save re-read nothing and reported a failure over a record
             * that was safely stored. A red error after a success reads as data loss
             * — the worst thing this screen can say, since its whole job is making a
             * scheduler confident the evidence was captured.
             */
            if (identity) { setError(null); return; }
            setError('Nothing was scanned. Pull the trigger with the cursor in the box.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const res = await scanLicence(await getToken(), checkIn.checkInId, raw, confirmed);
            setIdentity(res.identity);
            setFindings(res.findings);
            setRecognised(res.recognised);
            setReplace(null);
            onSaved(res.identity);
        } catch (err) {
            if (err instanceof ApiCallError && err.details.code === 'DifferentDriver') {
                // ⛔ NOT a silent replace. The scheduler is told whose record this
                // is, and replacing it is a second, deliberate act that the server
                // logs with both names.
                setReplace({
                    message: err.details.error,
                    retry: (c: boolean) => submitScan(c, raw),
                });
            } else {
                setError(describe(err));
                /* ⚠️ SET FROM THE SAME ERROR, so it can never describe a different
                   attempt from the message beside it. */
                setShape(err instanceof ApiCallError ? (err.details.shape ?? null) : null);
                setShapeCopied(false);
            }
        } finally {
            setBusy(false);
        }
    }

    async function submitManual(confirmed: boolean) {
        if (!checkIn) return;
        if (!mNumber.trim()) { setError('A license number is required — it is how we recognise him next time.'); return; }
        setBusy(true);
        setError(null);
        try {
            const res = await enterLicenceManually(await getToken(), checkIn.checkInId, {
                firstName: mFirst.trim() || null,
                lastName: mLast.trim() || null,
                licenceNumber: mNumber.trim(),
                licenceState: mState.trim().toUpperCase() || null,
                licenceExpiry: mExpiry.trim() || null,
                vehicleClass: mClass.trim().toUpperCase() || null,
                confirmed,
            });
            setIdentity(res.identity);
            setFindings(res.findings);
            setRecognised(res.recognised);
            setReplace(null);
            onSaved(res.identity);
        } catch (err) {
            if (err instanceof ApiCallError && err.details.code === 'DifferentDriver') {
                setReplace({ message: err.details.error, retry: (c: boolean) => submitManual(c) });
            } else {
                setError(describe(err));
                /* ⚠️ SET FROM THE SAME ERROR, so it can never describe a different
                   attempt from the message beside it. */
                setShape(err instanceof ApiCallError ? (err.details.shape ?? null) : null);
                setShapeCopied(false);
            }
        } finally {
            setBusy(false);
        }
    }

    const truck = checkIn
        ? `Arrival #${checkIn.arrivalSeq ?? '—'}${checkIn.driverName ? ` · ${checkIn.driverName}` : ''}`
        : '';

    return (
        <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Driver license</DialogTitle>
                    <DialogDescription>
                        {truck}
                        {checkIn?.direction === 'OUTBOUND' && (
                            // ⛔ Outbound is where a load can be driven away by the
                            // wrong man, so the asymmetry is stated rather than
                            // assumed (design §3).
                            <span className="ml-1 font-medium text-indigo-700">
                                · outbound — a license is required
                            </span>
                        )}
                    </DialogDescription>
                </DialogHeader>

                {/* ---- what we already know ------------------------------- */}
                {identity && (
                    <div className="space-y-2">
                        {recognised && (
                            <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-900">
                                We have scanned this license here before.
                            </div>
                        )}
                        <IdentityCard identity={identity} />
                    </div>
                )}

                {/* ---- findings -------------------------------------------- */}
                {findings.length > 0 && (
                    <div className="space-y-1">
                        {findings.map((f) => (
                            <div key={f.code} className={`rounded border px-2 py-1 text-xs ${FINDING_TONE(f)}`}>
                                {f.requiresOverride && <span className="mr-1 font-bold">⚠</span>}
                                {f.message}
                            </div>
                        ))}
                        {/* ⚠️ Said plainly, because a scheduler who thinks the
                            system is stopping him will stop reading these. */}
                        <div className="text-[11px] text-muted-foreground">
                            These do not stop a truck. Anything marked ⚠ has to be signed off before an
                            outbound load leaves, and the sign-off is recorded against the appointment
                            with your name on it.
                        </div>
                    </div>
                )}

                {/* ---- the replace confirmation ---------------------------- */}
                {replace && (
                    <div className="space-y-2 rounded border border-amber-400 bg-amber-50 p-2">
                        <div className="text-xs text-amber-900">{replace.message}</div>
                        <div className="text-[11px] text-amber-900">
                            Replacing is recorded with both names and your own. Do it if you scanned the
                            wrong card — not to move a license between two drivers.
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setReplace(null)}>
                                Keep the current driver
                            </Button>
                            <Button size="sm" disabled={busy} onClick={() => void replace.retry(true)}>
                                Replace
                            </Button>
                        </div>
                    </div>
                )}

                {/* ---- scan --------------------------------------------------- */}
                {mode === 'scan' ? (
                    <div className="space-y-1.5">
                        <Label htmlFor="licence-scan">Scan the BACK of the license</Label>
                        <div className="relative">
                            <textarea
                                id="licence-scan"
                                ref={scanRef}
                                rows={2}
                                autoComplete="off"
                                spellCheck={false}
                                disabled={busy || !!replace}
                                /**
                                 * ⛔ THE TEXT IS INVISIBLE, AND IT HAS TO BE. The decoded
                                 * string spells out a date of birth and a home address in
                                 * plain text, and a scheduler's screen faces a lobby. A
                                 * textarea cannot be type="password", so the characters are
                                 * rendered transparent instead — the count below is how a
                                 * person knows it is working, and copying is refused so the
                                 * invisible text cannot simply be lifted out.
                                 */
                                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2
                                           text-sm text-transparent caret-transparent selection:bg-transparent
                                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                                           disabled:cursor-not-allowed disabled:opacity-50"
                                onCopy={(e) => e.preventDefault()}
                                onCut={(e) => e.preventDefault()}
                                onChange={(e) => onScanInput(e.target.value)}
                                /**
                                 * ⛔ THIS HANDLER INSERTS; IT NEVER SUBMITS. Enter is a
                                 * character INSIDE the barcode — the element separator —
                                 * and submitting on it sent the server the first line and
                                 * nothing else (#226a). The burst still ends on a pause;
                                 * see BURST_GAP_MS. All this does is put back the newlines
                                 * Chrome swallows when the wedge sends them as Ctrl+J.
                                 */
                                onKeyDown={onScanKeyDown}
                            />
                            {chars === 0 && !busy && (
                                <span className="pointer-events-none absolute left-3 top-2 text-sm text-muted-foreground">
                                    Pull the trigger…
                                </span>
                            )}
                        </div>
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                            <span>
                                {busy
                                    ? 'Reading the license…'
                                    : chars > 0
                                        /**
                                          * ⚠️ "1 line" IS THE FAULT, SAID PLAINLY. A real
                                          * licence is 25-30 lines; one line means the
                                          * separators were swallowed and the scan will be
                                          * refused. Naming it here saves the round trip.
                                          */
                                        ? `${chars} characters · ${lineCount} line${lineCount === 1 ? '' : 's'}`
                                          + (lineCount === 1 ? ' — no line breaks arrived' : '')
                                        : 'The large rectangular barcode, not the small one.'}
                            </span>
                            <button
                                type="button"
                                className="underline"
                                disabled={busy}
                                onClick={() => { clearBurst(); readAndWipe(); setMode('manual'); setError(null); }}
                            >
                                The card will not scan
                            </button>
                        </div>
                    </div>
                ) : (
                    /* ---- manual ------------------------------------------- */
                    <div className="space-y-2">
                        <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] text-slate-700">
                            Typed details are recorded as typed, not scanned. That is allowed — a driver
                            whose card will not read is still a real driver — but the record will say so.
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                                <Label htmlFor="lic-first">First name</Label>
                                <Input id="lic-first" value={mFirst} onChange={(e) => setMFirst(e.target.value)} disabled={busy} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="lic-last">Last name</Label>
                                <Input id="lic-last" value={mLast} onChange={(e) => setMLast(e.target.value)} disabled={busy} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="lic-num">License number *</Label>
                                <Input id="lic-num" value={mNumber} onChange={(e) => setMNumber(e.target.value)} disabled={busy} autoFocus />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="lic-state">State</Label>
                                <Input id="lic-state" value={mState} onChange={(e) => setMState(e.target.value)} disabled={busy} maxLength={10} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="lic-exp">Expires</Label>
                                <Input id="lic-exp" type="date" value={mExpiry} onChange={(e) => setMExpiry(e.target.value)} disabled={busy} />
                            </div>
                            <div className="space-y-1">
                                <Label htmlFor="lic-class">Class</Label>
                                <Input id="lic-class" value={mClass} onChange={(e) => setMClass(e.target.value)} disabled={busy} maxLength={20} placeholder="A, B, C…" />
                            </div>
                        </div>
                        <button
                            type="button"
                            className="text-[11px] underline text-muted-foreground"
                            disabled={busy}
                            onClick={() => { setMode('scan'); setError(null); }}
                        >
                            Back to scanning
                        </button>
                    </div>
                )}

                {error && (
                    <div className="rounded border border-destructive/40 bg-destructive/5 px-2 py-1 text-xs text-destructive">
                        {error}
                    </div>
                )}

                {/**
                  * ⭐ THE DIAGNOSTIC, UNDER THE MESSAGE AND DELIBERATELY QUIET.
                  *
                  * ⚠️ IT IS NOT ADVICE AND MUST NOT LOOK LIKE ADVICE. The sentence
                  * above tells the scheduler what to DO; this tells whoever he
                  * sends it to what HAPPENED. Styled as muted monospace so the eye
                  * goes to the instruction first.
                  * ⛔ NO VALUES - see `describeBarcode`. That is what makes it
                  * pasteable, and a diagnostic nobody is allowed to send is not a
                  * diagnostic.
                  */}
                {shape && (
                    <div className="mt-1 rounded border border-border bg-muted/30 px-2 py-1">
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                                Scan details (no personal data)
                            </span>
                            <button
                                type="button"
                                onClick={() => {
                                    /* ⚠️ clipboard can reject on an insecure origin or a
                                       denied permission. A copy button that silently
                                       does nothing is worse than one that says so. */
                                    void navigator.clipboard.writeText(shape)
                                        .then(() => setShapeCopied(true))
                                        .catch(() => setShapeCopied(false));
                                }}
                                className="shrink-0 text-[10px] underline underline-offset-2 hover:no-underline"
                            >
                                {shapeCopied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                        <div className="mt-0.5 break-all font-mono text-[10px] leading-snug text-muted-foreground">
                            {shape}
                        </div>
                    </div>
                )}

                {/**
                  * ⭐ SAY IT WAS SAVED, OUT LOUD. Until now the identity card appearing
                  * was the ONLY signal anything had been stored, and Pete had to ask
                  * whether it had (#236). One line costs nothing and removes the
                  * question — the scan is silent and instant, so the screen has to
                  * carry the confirmation the keystroke never gave.
                  */}
                {identity && mode === 'scan' && !replace && !busy && (
                    <div className="rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-900">
                        Saved. Nothing is pending — you can close this.
                    </div>
                )}

                <DialogFooter>
                    <div className="flex w-full justify-end gap-2">
                        {/**
                          * ⛔ THE PRIMARY ACTION CHANGES ONCE THERE IS A LICENCE ON FILE.
                          *
                          * ⚠️ "Save license" sitting there dark and prominent after the
                          * burst has already saved is an invitation to press a button
                          * that can only produce an error. Closing changes nothing:
                          * there is nothing pending to lose.
                          *
                          * ⭐ BUT SAVE MUST STILL EXIST FOR THE CASE IT WAS BUILT FOR —
                          * a burst that arrives WITHOUT the AAMVA header is deliberately
                          * not auto-submitted (a fragment must not be sent as a
                          * licence), and a scheduler needs a way to send it anyway. So
                          * it returns the moment the box has something in it.
                          */}
                        {identity && mode === 'scan' && chars === 0 ? (
                            <Button size="sm" onClick={onClose} disabled={busy}>
                                Done
                            </Button>
                        ) : (
                            <>
                                <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
                                    Close
                                </Button>
                                <Button
                                    size="sm"
                                    disabled={busy || !!replace}
                                    onClick={() => void (mode === 'scan' ? submitScan(false) : submitManual(false))}
                                >
                                    {busy ? 'Saving…' : 'Save license'}
                                </Button>
                            </>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
